import { BondingFormModal } from '@/components/admin/BondingFormModal';
import { EndpointFormModal } from '@/components/admin/EndpointFormModal';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { LlmBonding, LlmEndpoint, Provider } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  MoreHorizontal,
  Pencil,
  Power,
  Star,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatRelative } from './EndpointDetail';

const PROVIDER_LABELS: Record<Provider, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  gemini: 'Gemini',
  gcp: 'GCP Vertex AI (Gemini)',
  'gcp-anthropic': 'GCP Vertex AI (Claude)',
  vllm: 'vLLM',
  mlx: 'mlx',
  ollama: 'Ollama',
  'lm-studio': 'LM Studio',
  'openai-compatible': 'OpenAI 호환',
};

interface Props {
  id: string;
}

export function AdminLlmBondingDetail({ id }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editBondingOpen, setEditBondingOpen] = useState(false);
  const [editMember, setEditMember] = useState<LlmEndpoint | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'llm-bondings', id],
    queryFn: () => api.adminLlmBondingGet(id),
  });

  const updateBondingMut = useMutation({
    mutationFn: (body: Parameters<typeof api.adminLlmBondingUpdate>[1]) =>
      api.adminLlmBondingUpdate(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'llm-bondings'] }),
    onError: (err: unknown) =>
      setServerError(err instanceof ApiError ? err.message : '수정에 실패했습니다.'),
  });

  const deleteBondingMut = useMutation({
    mutationFn: () => api.adminLlmBondingDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'llm-bondings'] });
      navigate({ to: '/admin/llm/bondings' });
    },
    onError: (err: unknown) =>
      setServerError(err instanceof ApiError ? err.message : '삭제에 실패했습니다.'),
  });

  const probeMut = useMutation({
    mutationFn: () => api.adminLlmBondingProbe(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'llm-bondings'] });
      qc.invalidateQueries({ queryKey: ['admin', 'llm-endpoints'] });
    },
    onError: (err: unknown) =>
      setServerError(err instanceof ApiError ? err.message : '연결 테스트에 실패했습니다.'),
  });

  const probeMemberMut = useMutation({
    mutationFn: (memberId: string) => api.adminLlmProbe(memberId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'llm-bondings'] });
      qc.invalidateQueries({ queryKey: ['admin', 'llm-endpoints'] });
    },
    onError: (err: unknown) =>
      setServerError(err instanceof ApiError ? err.message : '멤버 연결 테스트에 실패했습니다.'),
  });

  const updateMemberMut = useMutation({
    mutationFn: ({
      memberId,
      body,
    }: {
      memberId: string;
      body: Parameters<typeof api.adminLlmUpdate>[1];
    }) => api.adminLlmUpdate(memberId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'llm-bondings'] });
      qc.invalidateQueries({ queryKey: ['admin', 'llm-endpoints'] });
    },
    onError: (err: unknown) =>
      setServerError(err instanceof ApiError ? err.message : '멤버 수정에 실패했습니다.'),
  });

  const removeMemberMut = useMutation({
    mutationFn: (memberId: string) => api.adminLlmBondingRemoveMember(id, { memberId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'llm-bondings'] }),
    onError: (err: unknown) =>
      setServerError(err instanceof ApiError ? err.message : '멤버 제거에 실패했습니다.'),
  });

  return (
    <PageShell
      header={
        <>
          <button
            type="button"
            onClick={() => navigate({ to: '/admin/llm/bondings' })}
            className="mb-4 inline-flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" />
            LLM 본딩 목록
          </button>
          <PageHeader
            title={data ? (data.displayName ?? data.name) : '본딩'}
            description={data?.description ?? undefined}
          />
        </>
      }
    >
      {serverError ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {serverError}
          <button
            type="button"
            onClick={() => setServerError(null)}
            className="ml-3 text-xs underline"
          >
            닫기
          </button>
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : '본딩을 불러오지 못했습니다.'}
        </p>
      ) : data ? (
        <div className="space-y-4">
          <BondingActionsBar
            bonding={data}
            onEdit={() => setEditBondingOpen(true)}
            onProbe={() => probeMut.mutate()}
            probing={probeMut.isPending}
            onToggleEnabled={() => updateBondingMut.mutate({ isEnabled: !data.isEnabled })}
            onToggleEnabledForUsers={() =>
              updateBondingMut.mutate({ isEnabledForUsers: !data.isEnabledForUsers })
            }
            onToggleDefault={() =>
              data.isDefault ? undefined : updateBondingMut.mutate({ isDefault: true })
            }
            onDelete={() => {
              if (window.confirm(`본딩 "${data.displayName ?? data.name}" 을 삭제할까요?`)) {
                deleteBondingMut.mutate();
              }
            }}
          />

          <BondingInfoCard bonding={data} />

          <MembersSection
            members={data.members}
            canRemove={data.members.length > 2}
            onNavigate={(memberId) =>
              navigate({ to: '/admin/llm/endpoints/$id', params: { id: memberId } })
            }
            onProbe={(memberId) => probeMemberMut.mutate(memberId)}
            probingId={probeMemberMut.isPending ? probeMemberMut.variables : null}
            onEdit={(member) => setEditMember(member)}
            onToggleEnabled={(member) =>
              updateMemberMut.mutate({
                memberId: member.id,
                body: { isEnabledForUsers: !member.isEnabledForUsers },
              })
            }
            onRemove={(member) => {
              if (
                window.confirm(
                  `"${member.displayName ?? member.name}" 을 본딩에서 제거할까요? (엔드포인트 자체는 유지)`,
                )
              ) {
                removeMemberMut.mutate(member.id);
              }
            }}
          />
        </div>
      ) : null}

      <BondingFormModal
        open={editBondingOpen}
        mode={editBondingOpen && data ? { kind: 'edit', bonding: data } : null}
        onClose={() => setEditBondingOpen(false)}
      />
      <EndpointFormModal
        open={!!editMember}
        mode={editMember ? { kind: 'edit', endpoint: editMember } : null}
        onClose={() => setEditMember(null)}
      />
    </PageShell>
  );
}

function BondingActionsBar({
  bonding,
  onEdit,
  onProbe,
  probing,
  onToggleEnabled,
  onToggleEnabledForUsers,
  onToggleDefault,
  onDelete,
}: {
  bonding: LlmBonding;
  onEdit: () => void;
  onProbe: () => void;
  probing: boolean;
  onToggleEnabled: () => void;
  onToggleEnabledForUsers: () => void;
  onToggleDefault: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button onClick={onEdit}>
        <Pencil className="size-3.5" />
        편집
      </Button>
      <Button variant="secondary" onClick={onProbe} disabled={probing}>
        {probing ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <CheckCircle2 className="size-3.5" />
        )}
        모든 멤버 연결 테스트
      </Button>
      <Button variant="secondary" onClick={onToggleEnabled}>
        <Power className="size-3.5" />
        {bonding.isEnabled ? '본딩 비활성화' : '본딩 활성화'}
      </Button>
      <Button variant="secondary" onClick={onToggleEnabledForUsers}>
        {bonding.isEnabledForUsers ? '사용자 노출 해제' : '사용자에게 노출'}
      </Button>
      <Button variant="secondary" onClick={onToggleDefault} disabled={bonding.isDefault}>
        <Star className="size-3.5" />
        {bonding.isDefault ? '기본 모델 (현재)' : '기본 모델로 지정'}
      </Button>
      <Button variant="ghost" onClick={onDelete} className="text-destructive">
        <Trash2 className="size-3.5" />
        삭제
      </Button>
    </div>
  );
}

function BondingInfoCard({ bonding }: { bonding: LlmBonding }) {
  return (
    <section className="rounded-2xl border border-border bg-surface px-4 py-3">
      <h2 className="text-xs uppercase tracking-wider text-foreground-muted mb-2">정보</h2>
      <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-[11px] uppercase tracking-wider text-foreground-muted">이름</dt>
          <dd className="mt-0.5 font-mono">{bonding.name}</dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wider text-foreground-muted">표시명</dt>
          <dd className="mt-0.5">{bonding.displayName ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wider text-foreground-muted">정렬 순서</dt>
          <dd className="mt-0.5">{bonding.sortOrder}</dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wider text-foreground-muted">
            라우팅 전략
          </dt>
          <dd className="mt-0.5">
            {bonding.routingStrategy === 'priority' ? (
              <Badge tone="accent">우선순위 (위에서부터)</Badge>
            ) : (
              <Badge tone="mute">랜덤 균등 분배</Badge>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wider text-foreground-muted">플래그</dt>
          <dd className="mt-0.5 flex flex-wrap items-center gap-1.5">
            {bonding.isDefault ? <Badge tone="accent">기본</Badge> : null}
            {bonding.isEnabled ? (
              bonding.isEnabledForUsers ? (
                <Badge tone="ok">사용자 노출</Badge>
              ) : (
                <Badge tone="mute">숨김</Badge>
              )
            ) : (
              <Badge tone="warn">비활성</Badge>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wider text-foreground-muted">생성</dt>
          <dd className="mt-0.5">{new Date(bonding.createdAt).toLocaleString()}</dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wider text-foreground-muted">수정</dt>
          <dd className="mt-0.5">{new Date(bonding.updatedAt).toLocaleString()}</dd>
        </div>
      </dl>
    </section>
  );
}

function MembersSection({
  members,
  canRemove,
  onNavigate,
  onProbe,
  probingId,
  onEdit,
  onToggleEnabled,
  onRemove,
}: {
  members: LlmEndpoint[];
  canRemove: boolean;
  onNavigate: (memberId: string) => void;
  onProbe: (memberId: string) => void;
  probingId: string | null;
  onEdit: (member: LlmEndpoint) => void;
  onToggleEnabled: (member: LlmEndpoint) => void;
  onRemove: (member: LlmEndpoint) => void;
}) {
  return (
    <section className="rounded-2xl border border-border bg-surface overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-surface-muted/40 border-b border-border">
        <h2 className="text-xs uppercase tracking-wider text-foreground-muted">
          멤버 ({members.length})
        </h2>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-surface-muted/40 text-[11px] uppercase tracking-wider text-foreground-muted">
          <tr>
            <Th>이름</Th>
            <Th>모델</Th>
            <Th>상태</Th>
            <Th align="right">액션</Th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <MemberRow
              key={m.id}
              member={m}
              canRemove={canRemove}
              probing={probingId === m.id}
              onNavigate={() => onNavigate(m.id)}
              onProbe={() => onProbe(m.id)}
              onEdit={() => onEdit(m)}
              onToggleEnabled={() => onToggleEnabled(m)}
              onRemove={() => onRemove(m)}
            />
          ))}
          {members.length === 0 ? (
            <tr>
              <td colSpan={4} className="text-center text-foreground-muted py-10">
                멤버가 없습니다.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </section>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      scope="col"
      className={cn('px-4 py-2.5 font-medium', align === 'right' ? 'text-right' : 'text-left')}
    >
      {children}
    </th>
  );
}

function MemberRow({
  member,
  canRemove,
  probing,
  onNavigate,
  onProbe,
  onEdit,
  onToggleEnabled,
  onRemove,
}: {
  member: LlmEndpoint;
  canRemove: boolean;
  probing: boolean;
  onNavigate: () => void;
  onProbe: () => void;
  onEdit: () => void;
  onToggleEnabled: () => void;
  onRemove: () => void;
}) {
  return (
    <tr className="border-t border-border hover:bg-surface-muted/30">
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={onNavigate}
          className="text-left hover:underline font-medium"
        >
          {member.displayName ?? member.name}
        </button>
        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-foreground-muted">
          <span className="inline-flex items-center rounded-sm bg-surface-muted px-1.5 h-4 uppercase tracking-wider">
            {PROVIDER_LABELS[member.provider]}
          </span>
          <span className="font-mono">{member.name}</span>
        </div>
      </td>
      <td className="px-4 py-3 font-mono text-xs break-all">{member.model}</td>
      <td className="px-4 py-3">
        <StatusInline endpoint={member} probing={probing} />
      </td>
      <td className="px-4 py-3 text-right">
        <MemberMenu
          isEnabled={member.isEnabledForUsers}
          canRemove={canRemove}
          onProbe={onProbe}
          onEdit={onEdit}
          onToggleEnabled={onToggleEnabled}
          onRemove={onRemove}
        />
      </td>
    </tr>
  );
}

function StatusInline({ endpoint, probing }: { endpoint: LlmEndpoint; probing: boolean }) {
  if (probing) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-foreground-muted">
        <Loader2 className="size-3 animate-spin" />
        체크 중…
      </span>
    );
  }
  if (endpoint.lastProbedAt === null) {
    return <span className="text-xs text-foreground-muted">—</span>;
  }
  if (endpoint.lastProbeOk === true) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs">
        <CheckCircle2 className="size-3.5 text-success" />
        <span>{endpoint.lastProbeModelCount ?? 0}개 모델</span>
        <span className="text-foreground-muted">({formatRelative(endpoint.lastProbedAt)})</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <XCircle className="size-3.5 text-destructive" />
      <span className="text-destructive">실패</span>
      <span className="text-foreground-muted">({formatRelative(endpoint.lastProbedAt)})</span>
    </span>
  );
}

function MemberMenu({
  isEnabled,
  canRemove,
  onProbe,
  onEdit,
  onToggleEnabled,
  onRemove,
}: {
  isEnabled: boolean;
  canRemove: boolean;
  onProbe: () => void;
  onEdit: () => void;
  onToggleEnabled: () => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onScrollOrResize = () => setOpen(false);
    window.addEventListener('mousedown', handler);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen((v) => !v);
  };

  const close = () => setOpen(false);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="멤버 액션 메뉴"
        onClick={toggle}
        className="size-8 inline-grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
      >
        <MoreHorizontal className="size-4" />
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              style={{ position: 'fixed', top: pos.top, right: pos.right }}
              className="w-56 z-50 rounded-md border border-border bg-surface shadow-lg py-1 text-sm text-left"
            >
              <MenuItem
                onClick={() => {
                  close();
                  onProbe();
                }}
              >
                연결 테스트
              </MenuItem>
              <MenuItem
                onClick={() => {
                  close();
                  onEdit();
                }}
              >
                편집 (엔드포인트)
              </MenuItem>
              <MenuItem
                onClick={() => {
                  close();
                  onToggleEnabled();
                }}
              >
                {isEnabled ? '사용자 노출 해제' : '사용자에게 노출'}
              </MenuItem>
              <div className="my-1 border-t border-border" />
              <MenuItem
                danger
                disabled={!canRemove}
                onClick={() => {
                  close();
                  onRemove();
                }}
              >
                {canRemove ? '본딩에서 제거' : '본딩에서 제거 (멤버 최소 2개 필요)'}
              </MenuItem>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function MenuItem({
  children,
  onClick,
  disabled,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'block w-full text-left px-3 py-1.5 text-sm hover:bg-surface-muted',
        'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent',
        danger ? 'text-destructive' : '',
      )}
    >
      {children}
    </button>
  );
}

function Badge({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: 'accent' | 'ok' | 'mute' | 'warn';
}) {
  const cls = {
    accent: 'bg-accent/15 text-accent',
    ok: 'bg-success/15 text-success',
    mute: 'bg-surface-muted text-foreground-muted',
    warn: 'bg-destructive/15 text-destructive',
  }[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 h-5 rounded-sm text-[10px] uppercase tracking-wider',
        cls,
      )}
    >
      {children}
    </span>
  );
}
